/**
 * Client mirror of the IdP SMS region allowlist — CU-86ajwpnek (ported from
 * `mideo-health-app`, CU-86ajnb1qh §3a.5).
 *
 * ⚠️ THIS IS A MIRROR, NOT THE AUTHORITY. The real allowlist lives in the
 * Firebase console (Authentication → SMS region policy) and is edited BY HAND
 * over time (CPO additions, support-driven changes). This copy therefore WILL
 * drift from the server's.
 *
 * That drift is why §3a.5 mandates TWO layers:
 *   • Layer 1 (`isRegionAllowed`, pre-submission) — a UX optimization. Catches
 *     the common case early so the patient never waits on an SMS that will
 *     never arrive.
 *   • Layer 2 (`isRegionRejection`, post-error) — THE CORRECTNESS BOUNDARY. A
 *     number this mirror ALLOWS can still be rejected by the server, and that
 *     error must render the SAME friendly copy.
 *
 * Being conservative here is SAFE: an over-narrow mirror routes an eligible
 * patient to the friendly copy (which names support as the next step), it never
 * grants anything. An over-broad mirror simply defers to Layer 2. Neither
 * direction can bypass the possession proof.
 *
 * ⚠️ THE ADMIN CONSOLE IMPLEMENTS NEITHER LAYER, deliberately — its population
 * is overwhelmingly US, so it judged the mirror not worth the drift surface. Do
 * not "align with the console" by deleting these: the patient population is the
 * reason both layers exist here.
 *
 * ── WEB vs NATIVE ────────────────────────────────────────────────────────────
 * ⚠️ The mobile app reads BOTH `error.message` AND `error.nativeErrorMessage`,
 * because React Native Firebase puts the discriminating prose in the latter and
 * the former alone is not sufficient. `nativeErrorMessage` DOES NOT EXIST in the
 * Firebase JS SDK — the web error carries a single `message`. Callers here pass
 * `error.message`; there is no second field to combine. If a web ceremony ever
 * shows the message arriving empty where native had prose, that is a real
 * finding and not something to paper over with a looser predicate.
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * ISO 3166-1 alpha-2 codes mirroring the staging/prod IdP region allowlist.
 *
 * ⚠️ Do NOT enumerate this list in patient-visible copy, ClickUp, or compliance
 * artifacts (§3a.5): with a patient population this small, "the one country we
 * have a patient in" edges toward re-identifying information. It is
 * engineering-internal only.
 */
export const ALLOWED_SMS_REGIONS: readonly string[] = ['US', 'CA', 'AU'];

/**
 * Layer 1 — is this number in a region the server is expected to accept?
 *
 * Returns `true` when we cannot determine the country. Unparseable input is
 * NOT a region problem, and blocking it here would show region copy for what is
 * really a typo — the generic invalid-number path handles that instead.
 *
 * Expects E.164 (a leading "+"), which is what the enrollment form produces via
 * `parsePhone(value, country)`. Passing a locally-dialled number without the
 * "+" will simply fail to name a country and be allowed through to Layer 2.
 */
export function isRegionAllowed(e164Phone: string | null | undefined): boolean {
  if (!e164Phone) return true;
  try {
    const parsed = parsePhoneNumberFromString(e164Phone.trim());
    if (!parsed?.country) return true;
    return ALLOWED_SMS_REGIONS.includes(parsed.country);
  } catch {
    // Never let a parser fault masquerade as a policy decision.
    return true;
  }
}

/**
 * Layer 2 — does this IdP error look like a region rejection?
 *
 * Structured so that being WRONG is not a correctness failure: the caller maps
 * BOTH a positive match here AND any unrecognised send-failure to friendly
 * copy. If the true code is not in this set the patient still gets a helpful
 * message and a next step — the copy just degrades from region-specific to
 * generic. When a new code is observed, add it here and the copy sharpens;
 * nothing else needs to change.
 */
export function isRegionRejection(errorCode: string | undefined, errorMessage?: string | undefined): boolean {
  // ── ✅ MEASURED 2026-07-29 (on-device, staging, AU removed from the console
  // allowlist — the deliberate §3a.5 drift case). The real region rejection is:
  //
  //   code    : auth/operation-not-allowed
  //   message : "SMS unable to be sent until this region enabled by the app
  //             developer."
  //
  // ⚠️ THE CODE IS AMBIGUOUS AND MUST NOT BE MATCHED ALONE. The SAME
  // `auth/operation-not-allowed` is returned when the PHONE PROVIDER IS
  // DISABLED project-wide (measured 2026-07-28):
  //   message : "The given sign-in provider is disabled for this Firebase
  //             project."
  //
  // Those two demand OPPOSITE handling. A region rejection is the patient's
  // situation → friendly copy, no alert. A disabled provider is OUR
  // misconfiguration and a TOTAL SMS OUTAGE → it must reach `generic` →
  // captureException, or the outage is invisible in Sentry while every patient
  // is told "we can't text that number" and steered off SMS entirely.
  //
  // A previous `code.includes("not-allowed")` did exactly that and was removed.
  // Matching the MESSAGE is therefore load-bearing, not a nicety.
  //
  // Fails SAFE in both directions: an unmatched message falls through to
  // `generic`, which is still friendly copy with a next step, and a reworded
  // region message degrades to generic rather than misclassifying an outage.
  if (errorCode?.toLowerCase() === 'auth/operation-not-allowed') {
    const message = errorMessage?.toLowerCase() ?? '';
    // Short, stable fragments — resilient to the sentence being reworded around
    // them. Requires BOTH so the provider-disabled message cannot match.
    return message.includes('region') && message.includes('enabled');
  }

  if (!errorCode) return false;
  const code = errorCode.toLowerCase();
  // Exact matches below, never a "not-allowed" substring — see above.
  return (
    code.includes('region') ||
    code === 'auth/admin-restricted-operation' ||
    code === 'auth/unsupported-first-factor'
  );
}

/**
 * Is this the "verify your email before enrolling a second factor" rejection?
 *
 * ⚠️ MEASURED 2026-07-28 (on-device, staging). Identity Platform refuses MFA
 * enrollment until the account's email is verified. Two things make this awkward
 * and are the reason this predicate reads the MESSAGE, not just the code:
 *
 *  1. The backend returns **`auth/unknown`** — NOT the `auth/unverified-email`
 *     code that exists in the Firebase SDKs. The only reliable signal is the
 *     prose: "Need to verify email first before enrolling second factors."
 *  2. `auth/unknown` is a WILDCARD. Matching it alone would misclassify every
 *     other unmapped backend error as an email problem, so the message fragment
 *     is what actually discriminates — the code is not sufficient on its own.
 *
 * ⚠️ Classifying on the code alone falls through to `generic` → "try again in a
 * moment", advice that can NEVER work: the patient must go and click a link in
 * their inbox, and no amount of retrying changes that.
 *
 * Message matching is inherently fragile (Firebase can reword). Structured so
 * that being wrong DEGRADES rather than breaks: an unmatched error falls through
 * to `generic`, which is still friendly copy with a next step. The proper
 * `auth/`-code match is kept too, so if the backend ever starts returning it
 * correctly this keeps working without a change.
 */
export function isUnverifiedEmailRejection(
  errorCode: string | undefined,
  errorMessage?: string | undefined,
): boolean {
  if (errorCode?.toLowerCase() === 'auth/unverified-email') return true;
  if (!errorMessage) return false;
  const message = errorMessage.toLowerCase();
  // Short, stable fragments — resilient to the sentence being reworded around
  // them. Requires BOTH so an unrelated message mentioning "verify" can't match.
  return message.includes('verify email') && message.includes('second factor');
}

/**
 * Is this a MALFORMED number rather than a policy rejection?
 *
 * ⚠️ Deliberately NOT part of `isRegionRejection`. `auth/invalid-phone-number`
 * means "that isn't a valid number" — a TYPO — not "your country isn't
 * supported". Conflating them mis-routes an ordinary fat-finger into the
 * region path, which tells the patient their region is unsupported and steers
 * them away from SMS entirely (to passkey/support) instead of simply asking
 * them to correct the digits. Wrong copy AND a wrong next step.
 */
export function isMalformedPhoneError(errorCode: string | undefined): boolean {
  if (!errorCode) return false;
  return errorCode.toLowerCase() === 'auth/invalid-phone-number';
}
